import React, { useEffect, useRef } from "react"
import { animate, eases } from "animejs"
import { getFontSize } from "@/utils/adaptFont"

// ==================== 字体缩放辅助函数 ====================
/** 将设计稿 px 值转换为考虑字体缩放后的值 */
const scalePx = (px: number): number => {
    const baseFontSize = 16
    const currentFontSize = getFontSize()
    return px * (currentFontSize / baseFontSize)
}

// ==================== 动画时长常量 ====================
/** icon旋转动画时长（毫秒）- 与Preloader中的常量保持一致 */
export const ICON_ROTATION_DURATION = 600
/** 进度条渐隐动画时长（毫秒）- 与Preloader中的常量保持一致 */
export const PROGRESS_EXIT_DURATION = 800

/** 幕布进入动画时长（毫秒）- 从四周向中心收缩，是所有进入动画中最长的 */
export const MASK_ENTRY_DURATION = 1000
/** 幕布退出动画时长（毫秒）- 从中心向四周展开 */
export const MASK_EXIT_DURATION = 800

/** 标题背景进入动画时长（毫秒） */
export const TITLE_BG_ENTRY_DURATION = 300
/** 标题文字进入动画时长（毫秒） */
export const TITLE_ENTRY_DURATION = 0
/** 标题文字进入动画延迟（毫秒） */
export const TITLE_ENTRY_DELAY = 0

/** 角色进入动画时长（毫秒） */
export const ROLE_ENTRY_DURATION = 600
/** 角色1进入动画延迟（毫秒） */
export const ROLE1_ENTRY_DELAY = 300
/** 角色2进入动画延迟（毫秒） */
export const ROLE2_ENTRY_DELAY = 700

/** 角色摆动动画周期时长（毫秒） */
export const ROLE_SWING_DURATION = 3000

/** 页面退出动画时长（毫秒） */
export const PAGE_EXIT_DURATION = 500
/** 页面退出动画延迟（毫秒）- 让幕布动画先开始 */
export const PAGE_EXIT_DELAY = 100

/** 绳子进入动画时长（毫秒）- 从上到下垂下 */
export const ROPE_ENTRY_DURATION = 2000
/** 绳子退出动画时长（毫秒）- 从下到上收起 */
export const ROPE_EXIT_DURATION = 2000
/** 绳子摆动动画周期时长（毫秒） */
export const ROPE_SWING_DURATION = 4000

/** 竹子进入动画时长（毫秒） */
export const BAMBOO_ENTRY_DURATION = 1500
/** 竹子摆动动画周期时长（毫秒） */
export const BAMBOO_SWING_DURATION = 10000
/** 竹子摆动角度范围（度）- 左右摆动幅度 */
export const BAMBOO_SWING_ANGLE = 1

/** 棋子旋转动画周期时长（毫秒） */
export const CHESS_ROTATION_DURATION = 4000
/** 棋子旋转角度（度）- 每次旋转360度 */
export const CHESS_ROTATION_ANGLE = 360

// ==================== 自定义 Hooks ====================

/**
 * 幕布进入动画 - 从四周由外向内收缩，如同幕布从外拉起
 * 使用 scale 从 2 缩放到 1，配合 transform-origin: center bottom
 * 视觉效果：幕布从四周向中心收缩，底部作为锚点
 */
export const useMaskEntryAnimation = (
    maskRef: React.RefObject<HTMLDivElement>
) => {
    useEffect(() => {
        const mask = maskRef.current
        if (!mask) return

        // 初始状态：放大状态，从四周开始（幕布覆盖整个屏幕）
        mask.style.transform = "scale(2)"
        mask.style.transformOrigin = "center bottom"

        // 动画：从四周向中心收缩（scale 从 2 到 1）
        // 由于 transform-origin 在底部中心，看起来像从四周向中心收缩
        // 幕布进入动画时长最长，营造缓慢拉开的效果
        const control = { scale: 2 }
        animate(control, {
            scale: [2, 1],
            duration: MASK_ENTRY_DURATION,
            ease: eases.outCubic,
            onUpdate: () => {
                mask.style.transform = `scale(${control.scale})`
            },
        })
    }, [maskRef])
}

/**
 * 幕布退出动画 - 从中心向四周展开
 * 使用 scale 从 1 放大到 2，视觉效果是从中心向四周展开
 */
export const useMaskExitAnimation = (
    maskRef: React.RefObject<HTMLDivElement>
) => {
    const exitAnimRef = useRef<ReturnType<typeof animate> | null>(null)

    const startExit = () => {
        const mask = maskRef.current
        if (!mask) return

        // 如果已有动画，先暂停
        if (exitAnimRef.current) {
            exitAnimRef.current.pause()
        }

        // 确保 transform-origin 设置为底部中心
        mask.style.transformOrigin = "center bottom"

        // 获取当前的 scale 值，默认为 1
        const currentTransform = mask.style.transform || "scale(1)"
        const match = currentTransform.match(/scale\(([\d.]+)\)/)
        const currentScale = match ? parseFloat(match[1]) : 1

        // 动画：从中心向四周展开（scale 从当前值到 1.8）
        // 视觉效果：幕布从中心向四周展开，覆盖整个屏幕
        // 退出时幕布动画最先开始，时长较长以便与页面退出动画配合
        const control = { scale: currentScale }
        exitAnimRef.current = animate(control, {
            scale: [currentScale, 2],
            duration: MASK_EXIT_DURATION,
            ease: eases.inQuad,
            onUpdate: () => {
                mask.style.transform = `scale(${control.scale})`
            },
        })
    }

    // 清理函数
    useEffect(() => {
        return () => {
            if (exitAnimRef.current) {
                exitAnimRef.current.pause()
                exitAnimRef.current = null
            }
        }
    }, [])

    return startExit
}

/**
 * 进入页面时的动画
 * 包括：标题背景、标题文字、两个角色的进入动画
 */
export const useEntryAnimation = (
    titleBgRef: React.RefObject<HTMLDivElement>,
    titleRef: React.RefObject<HTMLDivElement>,
    role1Ref: React.RefObject<HTMLDivElement> | null,
    role2Ref: React.RefObject<HTMLDivElement>,
    subTitleRef: React.RefObject<HTMLDivElement>
) => {
    useEffect(() => {
        const titleBg = titleBgRef.current
        const title = titleRef.current
        const role1 = role1Ref?.current
        const role2 = role2Ref.current
        const subTitle = subTitleRef.current

        if (!titleBg || !title || !role2 || !subTitle) return

        // 初始状态：隐藏所有元素
        titleBg.style.opacity = "0"
        titleBg.style.transform = "translate(-50%, -40%) scale(0.9)"
        title.style.opacity = "0"
        title.style.transform = `translateX(-50%) translateY(${scalePx(30)}px)`
        if (role1) {
            role1.style.opacity = "1"
            role1.style.transform = "translateX(0px)"
        }
        // role2 按原逻辑：从右向左滑入
        role2.style.opacity = "0"
        const initialTranslateXForRole2 = scalePx(100)
        role2.style.transform = `translateX(${initialTranslateXForRole2}px)`
        // subTitle 初始：向上位移 -3rem
        const initialUpRemPx = -scalePx(10) // -3rem
        subTitle.style.opacity = "0"
        subTitle.style.transform = `translateY(${initialUpRemPx}px)`

        // 标题背景：淡入 + 缩放
        const titleBgControl = { opacity: 0, scale: 0.9 }
        animate(titleBgControl, {
            opacity: [0, 1],
            scale: [0.9, 1],
            duration: TITLE_BG_ENTRY_DURATION,
            ease: eases.outQuad,
            onUpdate: () => {
                titleBg.style.opacity = `${titleBgControl.opacity}`
                titleBg.style.transform = `translate(-50%, -40%) scale(${titleBgControl.scale})`
            },
        })

        // 标题文字：从下往上滑入 + 淡入
        setTimeout(() => {
            const initialTranslateY = scalePx(30)
            const titleControl = { opacity: 0, translateY: initialTranslateY }
            animate(titleControl, {
                opacity: [0, 1],
                translateY: [0, 0],
                duration: TITLE_ENTRY_DURATION,
                ease: eases.outCubic,
                onUpdate: () => {
                    title.style.opacity = `${titleControl.opacity}`
                    title.style.transform = `translateX(-50%) translateY(${titleControl.translateY}px)`
                },
            })
        }, TITLE_ENTRY_DELAY)

        // 角色1：从左侧滑入（已禁用动画，直接显示）
        // 动画已移除，role1 直接显示

        // 角色2：从右侧滑入；副标题：同时向下（Y）滑入
        setTimeout(() => {
            const initialTranslateX = initialTranslateXForRole2
            const role2Control = { opacity: 0, translateX: initialTranslateX }
            const subTitleControl = { opacity: 0, translateY: initialUpRemPx }
            animate(role2Control, {
                opacity: [0, 1],
                translateX: [initialTranslateX, 0],
                duration: ROLE_ENTRY_DURATION - 300,
                ease: eases.outCubic,
                onUpdate: () => {
                    role2.style.opacity = `${role2Control.opacity}`
                    role2.style.transform = `translateX(${role2Control.translateX}px)`
                },
            })
            // 分两段：第一段更长（70%），第二段（30%）
            const subTitleFirstDuration = Math.round(ROLE_ENTRY_DURATION * 0.7)
            const subTitleSecondDuration = ROLE_ENTRY_DURATION - subTitleFirstDuration
            // 第一段：上 -> 过冲到 24px
            animate(subTitleControl, {
                opacity: [0, 1, 1],
                translateY: [initialUpRemPx, scalePx(13)],
                duration: subTitleFirstDuration,
                ease: eases.outCubic,
                onUpdate: () => {
                    subTitle.style.opacity = `${subTitleControl.opacity}`
                    subTitle.style.transform = `translateY(${subTitleControl.translateY}px)`
                },
                onComplete: () => {
                    // 第二段：从 24px 回到 0
                    const subTitleControl2 = {
                        opacity: 1,
                        translateY: scalePx(13),
                    }
                    animate(subTitleControl2, {
                        opacity: [1, 1],
                        translateY: [scalePx(13), 0],
                        duration: subTitleSecondDuration,
                        ease: eases.outCubic,
                        onUpdate: () => {
                            subTitle.style.opacity = `${subTitleControl2.opacity}`
                            subTitle.style.transform = `translateY(${subTitleControl2.translateY}px)`
                        },
                    })
                },
            })
            // --- title 弹跳动画: scaleY 1 => 0.8 => 1 ---
            const bounceDuration = ROLE_ENTRY_DURATION // 可以与主标题动画同步时长
            const titleBounceControl = { scaleY: 1, translateY: 0, scaleX: 1 }
            // 标题弹跳改为两段：第一段更长（70%）
            const titleFirstDuration = Math.round(bounceDuration * 0.7)
            const titleSecondDuration = bounceDuration - titleFirstDuration
            // 第一段：scale 1 -> 0.9，translateY 0 -> 4px
            animate(titleBounceControl, {
                scaleY: [1, 0.95],
                scaleX: [1, 1.02],
                translateY: [0, scalePx(0)],
                duration: titleFirstDuration,
                ease: eases.outCubic,
                onBegin: () => {
                    title.style.transformOrigin = "center bottom"
                },
                onUpdate: () => {
                    title.style.transform = `translateX(-50%) translateY(${titleBounceControl.translateY}px) scaleY(${titleBounceControl.scaleY}) scaleX(${titleBounceControl.scaleX})`
                },
                onComplete: () => {
                    // 第二段：scale 0.9 -> 1，translateY 4px -> 0
                    const titleBounceControl2 = {
                        scaleY: 0.95,
                        scaleX: 1.02,
                        translateY: scalePx(0),
                    }
                    animate(titleBounceControl2, {
                        scaleY: [0.95, 1],
                        scaleX: [1.02, 1],
                        translateY: [scalePx(0), 0],
                        duration: titleSecondDuration,
                        ease: eases.outCubic,
                        onUpdate: () => {
                            title.style.transform = `translateX(-50%) translateY(${titleBounceControl2.translateY}px) scaleY(${titleBounceControl2.scaleY}) scaleX(${titleBounceControl2.scaleX})`
                        },
                    })
                },
            })
        }, ROLE2_ENTRY_DELAY)
    }, [titleBgRef, titleRef, role1Ref, role2Ref, subTitleRef])
}

/**
 * 角色摆动动画
 * 在开始按钮显示时，两个角色开始左右摆动
 * 延迟时间 = icon旋转时长 + 进度条渐隐时长 = 600ms + 800ms = 1400ms
 */
export const useRoleSwingAnimation = (
    _role1Ref: React.RefObject<HTMLDivElement> | null, // 已禁用 role1 动画
    role2Ref: React.RefObject<HTMLDivElement>,
    finished: boolean
) => {
    const role1SwingAnimRef = useRef<ReturnType<typeof animate> | null>(null)
    const role2SwingAnimRef = useRef<ReturnType<typeof animate> | null>(null)
    const swingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        // 延迟时间：等待icon旋转和进度条渐隐完成，与开始按钮显示时机同步
        const swingDelay = ICON_ROTATION_DURATION + PROGRESS_EXIT_DURATION
        // 清除之前的定时器
        if (swingTimeoutRef.current) {
            clearTimeout(swingTimeoutRef.current)
            swingTimeoutRef.current = null
        }

        // 停止所有动画
        if (role1SwingAnimRef.current) {
            role1SwingAnimRef.current.pause()
            role1SwingAnimRef.current = null
        }
        if (role2SwingAnimRef.current) {
            role2SwingAnimRef.current.pause()
            role2SwingAnimRef.current = null
        }

        if (!finished || !role2Ref.current) {
            return
        }

        // 延迟启动摆动动画，等待开始按钮显示
        swingTimeoutRef.current = setTimeout(() => {
            if (!role2Ref.current) return

            // 确保动画从 0 度开始：先设置初始状态为 rotate(0deg)
            // 保留原有的 translateX，添加 rotate(0deg)
            if (role2Ref.current) {
                const currentTransform = role2Ref.current.style.transform || ""
                const baseTransform =
                    currentTransform.replace(/rotate\([^)]*\)/g, "").trim() ||
                    "translateX(0px)"
                role2Ref.current.style.transform = `${baseTransform} rotate(0deg)`
            }

            // 角色1摆动动画已禁用

            // 角色2摆动动画（左右摆动，相位相反，营造互动感）
            const startRole2Swing = (isFirst = true) => {
                // 获取当前实际的 rotation 值，避免角度突变
                let currentRotation = 0
                if (!isFirst && role2Ref.current) {
                    const currentTransform =
                        role2Ref.current.style.transform || ""
                    const rotateMatch =
                        currentTransform.match(/rotate\(([^)]+)\)/)
                    if (rotateMatch) {
                        currentRotation = parseFloat(rotateMatch[1]) || 0
                    }
                }

                const role2Control = { rotation: currentRotation }
                // 根据是否是第一次播放，决定动画路径
                const rotationPath = isFirst
                    ? [0, 4, -4, 4] // 第一次：从 0 开始
                    : [4, -4, 4] // 后续：从 4 开始（但实际会从当前值平滑过渡）

                role2SwingAnimRef.current = animate(role2Control, {
                    rotation: rotationPath,
                    duration: ROLE_SWING_DURATION,
                    ease: eases.inOutSine,
                    onUpdate: () => {
                        if (role2Ref.current) {
                            // 保留原有的 translateX，只更新 rotate
                            const currentTransform =
                                role2Ref.current.style.transform || ""
                            const baseTransform =
                                currentTransform
                                    .replace(/rotate\([^)]*\)/g, "")
                                    .trim() || "translateX(0px)"
                            role2Ref.current.style.transform = `${baseTransform} rotate(${role2Control.rotation}deg)`
                        }
                    },
                    onComplete: () => {
                        // 循环播放
                        if (finished && role2Ref.current) {
                            startRole2Swing(false)
                        }
                    },
                })
            }

            // 启动摆动动画（role1 已禁用）
            startRole2Swing()
        }, swingDelay)

        // 清理函数：组件卸载或finished变为false时停止动画
        return () => {
            if (swingTimeoutRef.current) {
                clearTimeout(swingTimeoutRef.current)
                swingTimeoutRef.current = null
            }
            if (role1SwingAnimRef.current) {
                role1SwingAnimRef.current.pause()
                role1SwingAnimRef.current = null
            }
            if (role2SwingAnimRef.current) {
                role2SwingAnimRef.current.pause()
                role2SwingAnimRef.current = null
            }
        }
    }, [finished, role2Ref])
}

/**
 * 绳子进入动画 - 从上到下垂下
 * 使用 scaleY 从 0 缩放到 1，配合 transform-origin: top center
 * 视觉效果：绳子从顶部向下展开，如同垂下的效果
 * 动画完成后会触发回调，用于启动摆动动画
 */
export const useRopeEntryAnimation = (
    ropeRef: React.RefObject<HTMLDivElement>,
    onComplete?: () => void
) => {
    const entryAnimRef = useRef<ReturnType<typeof animate> | null>(null)
    const hasStartedRef = useRef(false)
    const onCompleteRef = useRef(onComplete)

    // 更新 onComplete 引用，但不触发重新执行
    useEffect(() => {
        onCompleteRef.current = onComplete
    }, [onComplete])

    useEffect(() => {
        const rope = ropeRef.current
        if (!rope || hasStartedRef.current) return

        // 标记动画已开始，防止重复执行
        hasStartedRef.current = true

        // 初始状态：垂直方向缩放为 0，从顶部开始
        rope.style.transform = "scaleY(0)"
        rope.style.transformOrigin = "top center"

        // 动画：从上到下垂下（scaleY 从 0 到 1）
        const control = { scaleY: 0 }
        entryAnimRef.current = animate(control, {
            scaleY: [0, 1],
            duration: ROPE_ENTRY_DURATION,
            ease: eases.outCubic,
            onUpdate: () => {
                rope.style.transform = `scaleY(${control.scaleY})`
            },
            onComplete: () => {
                // 动画完成后触发回调，用于启动摆动动画
                if (onCompleteRef.current) {
                    onCompleteRef.current()
                }
            },
        })

        // 清理函数
        return () => {
            if (entryAnimRef.current) {
                entryAnimRef.current.pause()
                entryAnimRef.current = null
            }
        }
    }, [ropeRef])
}

/**
 * 绳子摆动动画
 * 在进入动画完成后，绳子以顶部为轴心进行小幅度的左右摆动
 * 使用 rotate 实现，transform-origin: top center
 */
export const useRopeSwingAnimation = (
    ropeRef: React.RefObject<HTMLDivElement>,
    started: boolean
) => {
    const swingAnimRef = useRef<ReturnType<typeof animate> | null>(null)

    useEffect(() => {
        // 停止之前的动画
        if (swingAnimRef.current) {
            swingAnimRef.current.pause()
            swingAnimRef.current = null
        }

        if (!started || !ropeRef.current) {
            return
        }

        const rope = ropeRef.current

        // 确保 transform-origin 设置为顶部中心
        rope.style.transformOrigin = "top center"

        // 保留原有的 scaleY，添加 rotate(0deg)
        const currentTransform = rope.style.transform || "scaleY(1)"
        const baseTransform = currentTransform.replace(/rotate\([^)]*\)/g, "").trim() || "scaleY(1)"
        rope.style.transform = `${baseTransform} rotate(0deg)`

        // 绳子摆动动画（左右摆动）
        const startSwing = (isFirst = true) => {
            // 获取当前实际的 rotation 值，避免角度突变
            let currentRotation = 0
            if (!isFirst && ropeRef.current) {
                const currentTransform = ropeRef.current.style.transform || ""
                const rotateMatch = currentTransform.match(/rotate\(([^)]+)\)/)
                if (rotateMatch) {
                    currentRotation = parseFloat(rotateMatch[1]) || 0
                }
            }

            // 构建动画路径，确保路径的第一个值等于当前角度，实现无缝衔接
            // 小幅度摆动 ±2 度，形成一个完整的摆动周期：0 -> 2 -> -2 -> 2 -> -2
            let rotationPath: number[]
            if (isFirst) {
                // 第一次：从 0 开始完整周期
                rotationPath = [0, 2, -2, 2, -2]
            } else {
                // 后续循环：路径的第一个值必须是当前角度，确保无缝衔接
                // 标准摆动周期：0 -> 2 -> -2 -> 2 -> -2（循环）
                // 根据当前角度，构建从当前位置开始的完整周期
                const normalizeAngle = (angle: number) => {
                    // 将角度归一化到 [-2, 2] 范围内
                    if (angle > 2) return 2
                    if (angle < -2) return -2
                    return angle
                }

                const normalizedRotation = normalizeAngle(currentRotation)

                // 构建从当前角度开始的完整摆动周期
                // 标准的摆动序列是：... -2 -> 2 -> -2 -> 2 -> 0 -> 2 -> -2 ...
                // 根据当前角度，找到最近的摆动位置并继续
                if (normalizedRotation >= 1) {
                    // 当前在正角度区域，继续：当前 -> -2 -> 2 -> -2
                    rotationPath = [normalizedRotation, -2, 2, -2]
                } else if (normalizedRotation <= -1) {
                    // 当前在负角度区域，继续：当前 -> 2 -> -2 -> 2
                    rotationPath = [normalizedRotation, 2, -2, 2]
                } else {
                    // 当前在 0 附近，继续：当前 -> 2 -> -2 -> 2 -> -2
                    rotationPath = [normalizedRotation, 2, -2, 2, -2]
                }
            }

            const control = { rotation: currentRotation }
            swingAnimRef.current = animate(control, {
                rotation: rotationPath,
                duration: ROPE_SWING_DURATION,
                ease: eases.inOutSine,
                onUpdate: () => {
                    if (ropeRef.current) {
                        // 保留原有的 scaleY，只更新 rotate
                        const currentTransform = ropeRef.current.style.transform || ""
                        const baseTransform = currentTransform
                            .replace(/rotate\([^)]*\)/g, "")
                            .trim() || "scaleY(1)"
                        ropeRef.current.style.transform = `${baseTransform} rotate(${control.rotation}deg)`
                    }
                },
                onComplete: () => {
                    // 循环播放
                    if (started && ropeRef.current) {
                        startSwing(false)
                    }
                },
            })
        }

        // 启动摆动动画
        startSwing()

        // 清理函数：组件卸载或started变为false时停止动画
        return () => {
            if (swingAnimRef.current) {
                swingAnimRef.current.pause()
                swingAnimRef.current = null
            }
        }
    }, [started, ropeRef])
}

/**
 * 绳子退出动画 - 从下到上收起
 * 使用 scaleY 从 1 缩放到 0，视觉效果是从下到上收起
 */
export const useRopeExitAnimation = (
    ropeRef: React.RefObject<HTMLDivElement>
) => {
    const exitAnimRef = useRef<ReturnType<typeof animate> | null>(null)

    const startExit = () => {
        const rope = ropeRef.current
        if (!rope) return

        // 如果已有动画，先暂停
        if (exitAnimRef.current) {
            exitAnimRef.current.pause()
        }

        // 确保 transform-origin 设置为顶部中心
        rope.style.transformOrigin = "top center"

        // 获取当前的 scaleY 值，默认为 1
        const currentTransform = rope.style.transform || "scaleY(1)"
        const match = currentTransform.match(/scaleY\(([\d.]+)\)/)
        const currentScaleY = match ? parseFloat(match[1]) : 1

        // 动画：从下到上收起（scaleY 从当前值到 0）
        const control = { scaleY: currentScaleY }
        exitAnimRef.current = animate(control, {
            scaleY: [currentScaleY, 0],
            duration: ROPE_EXIT_DURATION,
            ease: eases.inQuad,
            onUpdate: () => {
                rope.style.transform = `scaleY(${control.scaleY})`
            },
        })
    }

    // 清理函数
    useEffect(() => {
        return () => {
            if (exitAnimRef.current) {
                exitAnimRef.current.pause()
                exitAnimRef.current = null
            }
        }
    }, [])

    return startExit
}

/**
 * 竹子进入动画 - 从下到上移动，带倾斜角度
 * 左侧竹子：从右下到左上（translateX 从正到负，translateY 从正到0，rotate 从正角度到0）
 * 右侧竹子：从左下到右上（translateX 从负到0，translateY 从正到0，rotate 从负角度到0）
 */
export const useBambooEntryAnimation = (
    bambooRef: React.RefObject<HTMLImageElement>,
    isLeft: boolean, // true 为左侧，false 为右侧
    onComplete?: () => void
) => {
    const entryAnimRef = useRef<ReturnType<typeof animate> | null>(null)
    const hasStartedRef = useRef(false)
    const onCompleteRef = useRef(onComplete)

    // 更新 onComplete 引用，但不触发重新执行
    useEffect(() => {
        onCompleteRef.current = onComplete
    }, [onComplete])

    useEffect(() => {
        const bamboo = bambooRef.current
        if (!bamboo || hasStartedRef.current) return

        // 标记动画已开始，防止重复执行
        hasStartedRef.current = true

        // 设置 transform-origin 为底部中心
        bamboo.style.transformOrigin = "bottom center"

        // 初始状态：根据左右侧设置不同的初始位置和角度
        const initialTranslateY = scalePx(0) // 从下方200px开始（设计稿值）
        const initialTranslateX = isLeft ? scalePx(50) : scalePx(-50) // 左侧从右，右侧从左
        const initialRotate = isLeft ? 0 : -0 // 左侧向右倾斜，右侧向左倾斜

        bamboo.style.transform = `translate(${initialTranslateX}px, ${initialTranslateY}px) rotate(${initialRotate}deg)`

        // 动画：移动到最终位置（translateX: 0, translateY: 0, rotate: 0）
        const control = {
            translateX: initialTranslateX,
            translateY: initialTranslateY,
            rotate: initialRotate,
        }
        entryAnimRef.current = animate(control, {
            translateX: [initialTranslateX, 0],
            translateY: [initialTranslateY, 0],
            rotate: [initialRotate, 0],
            duration: BAMBOO_ENTRY_DURATION,
            ease: eases.outCubic,
            onUpdate: () => {
                bamboo.style.transform = `translate(${control.translateX}px, ${control.translateY}px) rotate(${control.rotate}deg)`
            },
            onComplete: () => {
                // 动画完成后触发回调，用于启动摆动动画
                if (onCompleteRef.current) {
                    onCompleteRef.current()
                }
            },
        })

        // 清理函数
        return () => {
            if (entryAnimRef.current) {
                entryAnimRef.current.pause()
                entryAnimRef.current = null
            }
        }
    }, [bambooRef, isLeft])
}

/**
 * 竹子摆动动画
 * 在进入动画完成后，竹子以底部中心为圆点进行小幅度的左右摆动
 * 使用 rotate 实现，transform-origin: bottom center
 */
export const useBambooSwingAnimation = (
    bambooRef: React.RefObject<HTMLImageElement>,
    started: boolean
) => {
    const swingAnimRef = useRef<ReturnType<typeof animate> | null>(null)

    useEffect(() => {
        // 停止之前的动画
        if (swingAnimRef.current) {
            swingAnimRef.current.pause()
            swingAnimRef.current = null
        }

        if (!started || !bambooRef.current) {
            return
        }

        const bamboo = bambooRef.current

        // 确保 transform-origin 设置为底部中心
        bamboo.style.transformOrigin = "bottom center"

        // 保留原有的 transform，添加 rotate(0deg)
        const currentTransform = bamboo.style.transform || ""
        const baseTransform = currentTransform.replace(/rotate\([^)]*\)/g, "").trim() || ""
        bamboo.style.transform = baseTransform ? `${baseTransform} rotate(0deg)` : "rotate(0deg)"

        // 竹子摆动动画（左右摆动）
        const startSwing = (isFirst = true) => {
            // 获取当前实际的 rotation 值，避免角度突变
            let currentRotation = 0
            if (!isFirst && bambooRef.current) {
                const currentTransform = bambooRef.current.style.transform || ""
                const rotateMatch = currentTransform.match(/rotate\(([^)]+)\)/)
                if (rotateMatch) {
                    currentRotation = parseFloat(rotateMatch[1]) || 0
                }
            }

            // 构建动画路径，小幅度摆动，形成一个完整的摆动周期
            const swingAngle = BAMBOO_SWING_ANGLE
            const halfSwingAngle = swingAngle / 2
            let rotationPath: number[]
            if (isFirst) {
                // 第一次：从 0 开始完整周期
                rotationPath = [0, swingAngle, -swingAngle, swingAngle, -swingAngle]
            } else {
                // 后续循环：路径的第一个值必须是当前角度，确保无缝衔接
                const normalizeAngle = (angle: number) => {
                    // 将角度归一化到摆动范围内
                    if (angle > swingAngle) return swingAngle
                    if (angle < -swingAngle) return -swingAngle
                    return angle
                }

                const normalizedRotation = normalizeAngle(currentRotation)

                // 构建从当前角度开始的完整摆动周期
                if (normalizedRotation >= halfSwingAngle) {
                    // 当前在正角度区域，继续：当前 -> -swingAngle -> swingAngle -> -swingAngle
                    rotationPath = [normalizedRotation, -swingAngle, swingAngle, -swingAngle]
                } else if (normalizedRotation <= -halfSwingAngle) {
                    // 当前在负角度区域，继续：当前 -> swingAngle -> -swingAngle -> swingAngle
                    rotationPath = [normalizedRotation, swingAngle, -swingAngle, swingAngle]
                } else {
                    // 当前在 0 附近，继续：当前 -> swingAngle -> -swingAngle -> swingAngle -> -swingAngle
                    rotationPath = [normalizedRotation, swingAngle, -swingAngle, swingAngle, -swingAngle]
                }
            }

            const control = { rotation: currentRotation }
            swingAnimRef.current = animate(control, {
                rotation: rotationPath,
                duration: BAMBOO_SWING_DURATION,
                ease: eases.inOutSine,
                onUpdate: () => {
                    if (bambooRef.current) {
                        // 保留原有的 transform，只更新 rotate
                        const currentTransform = bambooRef.current.style.transform || ""
                        const baseTransform = currentTransform
                            .replace(/rotate\([^)]*\)/g, "")
                            .trim()
                        bambooRef.current.style.transform = baseTransform
                            ? `${baseTransform} rotate(${control.rotation}deg)`
                            : `rotate(${control.rotation}deg)`
                    }
                },
                onComplete: () => {
                    // 循环执行摆动动画
                    startSwing(false)
                },
            })
        }

        // 开始摆动动画
        startSwing(true)
    }, [bambooRef, started])
}

/**
 * 退出动画
 * 点击开始按钮后，整个loading页面淡出并缩放，同时幕布从中心向四周展开
 * 幕布和绳子退出动画最先开始，然后页面淡出动画才启动
 */
export const useExitAnimation = (
    loadingRef: React.RefObject<HTMLDivElement>,
    maskExitStart: () => void,
    ropeExitStart: () => void,
    onComplete: () => void
) => {
    const exitAnimRef = useRef<ReturnType<typeof animate> | null>(null)

    const startExit = () => {
        if (!loadingRef.current) return

        // 幕布和绳子退出动画最先开始
        maskExitStart()
        ropeExitStart()

        // 页面退出动画延迟启动，让幕布和绳子动画先开始
        // 延迟后开始页面淡出，确保幕布和绳子动画已经启动
        setTimeout(() => {
            if (!loadingRef.current) return

            // 如果已有动画，先暂停
            if (exitAnimRef.current) {
                exitAnimRef.current.pause()
            }

            const control = { opacity: 1, scale: 1 }
            exitAnimRef.current = animate(control, {
                opacity: [1, 0],
                scale: [1, 0.95],
                duration: PAGE_EXIT_DURATION,
                ease: eases.inQuad,
                onUpdate: () => {
                    if (loadingRef.current) {
                        loadingRef.current.style.opacity = `${control.opacity}`
                        loadingRef.current.style.transform = `scale(${control.scale})`
                    }
                },
                onComplete: () => {
                    onComplete()
                },
            })
        }, PAGE_EXIT_DELAY)
    }

    // 清理函数
    useEffect(() => {
        return () => {
            if (exitAnimRef.current) {
                exitAnimRef.current.pause()
                exitAnimRef.current = null
            }
        }
    }, [])

    return startExit
}

/**
 * 棋子翻转方向类型
 */
export type ChessDirection = "up" | "down" | "left" | "right"

/**
 * 棋子配置类型
 */
export interface ChessConfig {
    left: number | string
    top: number | string
    color: "white" | "black"
    direction: ChessDirection
}

/**
 * 棋子翻转和颜色切换动画
 * 支持上下左右四种翻转方向，并在翻转过程中切换黑白颜色
 * 支持多个棋子同时动画
 * @param chessRefs 棋子 ref 数组
 * @param configs 棋子配置数组
 * @param started 是否开始动画（页面加载完成后为 true）
 */
export const useChessRotationAnimation = (
    chessRefs: React.RefObject<HTMLImageElement>[],
    configs: ChessConfig[],
    started: boolean
) => {
    const flipAnimRefs = useRef<
        Map<HTMLImageElement, ReturnType<typeof animate>>
    >(new Map())
    const isWhiteRefs = useRef<Map<HTMLImageElement, boolean>>(new Map())

    useEffect(() => {
        // 如果未开始，不执行动画
        if (!started) return

        // 棋子图片路径
        const whitePiece = require("@/assets/imgs/loading/piece_white.png")
        const blackPiece = require("@/assets/imgs/loading/piece_black.png")

        // 根据方向确定使用的 scale 属性和初始值
        const getScaleConfig = (dir: ChessDirection) => {
            switch (dir) {
                case "up":
                    return { prop: "scaleY", start: 1, end: -1 } // 从下往上翻转
                case "down":
                    return { prop: "scaleY", start: -1, end: 1 } // 从上往下翻转
                case "left":
                    return { prop: "scaleX", start: 1, end: -1 } // 从右往左翻转
                case "right":
                    return { prop: "scaleX", start: -1, end: 1 } // 从左往右翻转
            }
        }

        // 获取棋子的当前颜色
        const getChessColor = (index: number): "white" | "black" => {
            const chessRef = chessRefs[index]
            const chess = chessRef?.current
            if (!chess) return configs[index]?.color || "white"

            // 从状态中获取颜色
            if (isWhiteRefs.current.has(chess)) {
                return isWhiteRefs.current.get(chess) ? "white" : "black"
            }
            // 如果状态中没有，从配置中获取
            return configs[index]?.color || "white"
        }

        // 选择下一个要翻转的棋子
        // 规则：选择与上一轮相反颜色的棋子，且不能是上一轮的棋子
        const selectNextChess = (
            lastIndex: number | null,
            lastColor: "white" | "black" | null
        ): number | null => {
            // 确定目标颜色（与上一轮相反）
            const targetColor: "white" | "black" =
                lastColor === "white" ? "black" : "white"

            // 获取所有符合条件（目标颜色且不是上一轮）的棋子索引
            const candidates: number[] = []
            for (let i = 0; i < chessRefs.length; i++) {
                if (i === lastIndex) continue // 跳过上一轮的棋子
                const color = getChessColor(i)
                if (color === targetColor) {
                    candidates.push(i)
                }
            }

            // 如果没有符合条件的棋子，选择其他颜色的棋子（仍然排除上一轮）
            if (candidates.length === 0) {
                for (let i = 0; i < chessRefs.length; i++) {
                    if (i === lastIndex) continue
                    candidates.push(i)
                }
            }

            // 如果还是没有候选（理论上不应该发生，除非只有一个棋子），返回 null
            if (candidates.length === 0) {
                return null
            }

            // 随机选择一个
            const randomIndex = Math.floor(Math.random() * candidates.length)
            return candidates[randomIndex]
        }

        // 上一轮的棋子索引和颜色
        let lastIndex: number | null = null
        let lastColor: "white" | "black" | null = null

        // 为每个棋子启动动画
        const startFlipForChess = (
            chess: HTMLImageElement,
            direction: ChessDirection,
            chessIndex: number
        ) => {
            // 初始化颜色状态（根据当前图片判断）
            if (!isWhiteRefs.current.has(chess)) {
                const isWhite = chess.src.includes("piece_white")
                isWhiteRefs.current.set(chess, isWhite)
            }

            // 设置 transform-origin 为中心点，确保翻转围绕中心
            chess.style.transformOrigin = "center center"

            const scaleConfig = getScaleConfig(direction)
            const isVertical = scaleConfig.prop === "scaleY"

            const startFlip = () => {
                // 获取当前的 scale 值
                let currentScale = scaleConfig.start
                const currentTransform = chess.style.transform || ""
                const scaleMatch = currentTransform.match(
                    new RegExp(`${scaleConfig.prop}\\(([^)]+)\\)`)
                )
                if (scaleMatch) {
                    currentScale = parseFloat(scaleMatch[1]) || scaleConfig.start
                }

                // 翻转动画：从 start 翻转到 end，再翻回 start
                // 每180度（即每次经过 scale=0）切换一次颜色
                const flipControl: { scaleY?: number; scaleX?: number } = {}
                if (isVertical) {
                    flipControl.scaleY = currentScale
                } else {
                    flipControl.scaleX = currentScale
                }

                let lastScale = currentScale // 记录上一次的 scale 值
                let hasSwitchedFirst = false // 标记是否已经切换过第一次
                let hasSwitchedSecond = false // 标记是否已经切换过第二次

                const anim = animate(flipControl, {
                    [scaleConfig.prop]: [currentScale, scaleConfig.end, scaleConfig.start],
                    duration: CHESS_ROTATION_DURATION,
                    ease: eases.inOutQuad,
                    onUpdate: () => {
                        if (chess) {
                            const currentScaleValue = isVertical
                                ? flipControl.scaleY ?? scaleConfig.start
                                : flipControl.scaleX ?? scaleConfig.start

                            // 构建 transform 字符串
                            // 对于垂直翻转，确保 scaleX 始终为 1（不影响宽度）
                            // 对于水平翻转，确保 scaleY 始终为 1（不影响高度）
                            let transform = ""
                            if (isVertical) {
                                // 垂直翻转：只改变 scaleY，scaleX 保持为 1
                                transform = `scaleX(1) scaleY(${currentScaleValue})`
                            } else {
                                // 水平翻转：只改变 scaleX，scaleY 保持为 1
                                transform = `scaleX(${currentScaleValue}) scaleY(1)`
                            }
                            chess.style.transform = transform

                            // 检测是否经过 scale = 0（翻转中间点）
                            // 当从正数变为负数时（第一次180度）
                            if (
                                !hasSwitchedFirst &&
                                lastScale > 0 &&
                                currentScaleValue <= 0
                            ) {
                                hasSwitchedFirst = true
                                const currentIsWhite =
                                    isWhiteRefs.current.get(chess) ?? true
                                const newIsWhite = !currentIsWhite
                                isWhiteRefs.current.set(chess, newIsWhite)
                                chess.src = newIsWhite ? whitePiece : blackPiece
                            }
                            // 当从负数变为正数时（第二次180度）
                            else if (
                                !hasSwitchedSecond &&
                                lastScale < 0 &&
                                currentScaleValue >= 0
                            ) {
                                hasSwitchedSecond = true
                                const currentIsWhite =
                                    isWhiteRefs.current.get(chess) ?? true
                                const newIsWhite = !currentIsWhite
                                isWhiteRefs.current.set(chess, newIsWhite)
                                chess.src = newIsWhite ? whitePiece : blackPiece
                            }

                            lastScale = currentScaleValue
                        }
                    },
                    onComplete: () => {
                        // 当前棋子动画完成，记录其颜色
                        const currentColor = getChessColor(chessIndex)

                        // 更新上一轮的信息
                        lastIndex = chessIndex
                        lastColor = currentColor

                        // 停止当前动画
                        const currentAnim = flipAnimRefs.current.get(chess)
                        if (currentAnim) {
                            currentAnim.pause()
                            flipAnimRefs.current.delete(chess)
                        }

                        // 选择下一个棋子
                        const nextIndex = selectNextChess(lastIndex, lastColor)

                        if (nextIndex !== null) {
                            const nextChessRef = chessRefs[nextIndex]
                            const nextConfig = configs[nextIndex]
                            const nextChess = nextChessRef.current

                            if (nextChess && nextConfig) {
                                // 重置 transform，确保从初始状态开始
                                const scaleConfig = getScaleConfig(nextConfig.direction)
                                if (scaleConfig.prop === "scaleY") {
                                    nextChess.style.transform = `scaleX(1) scaleY(${scaleConfig.start})`
                                } else {
                                    nextChess.style.transform = `scaleX(${scaleConfig.start}) scaleY(1)`
                                }

                                // 启动新选中的棋子动画
                                startFlipForChess(nextChess, nextConfig.direction, nextIndex)
                            }
                        }
                    },
                })

                // 保存动画引用
                flipAnimRefs.current.set(chess, anim)
            }

            // 启动动画
            startFlip()
        }

        // 初始化：随机选择一个棋子开始第一轮动画
        // 随机选择一个索引
        const initialIndex = Math.floor(Math.random() * chessRefs.length)

        const initialChessRef = chessRefs[initialIndex]
        const initialConfig = configs[initialIndex]
        const initialChess = initialChessRef.current

        if (initialChess && initialConfig) {
            startFlipForChess(initialChess, initialConfig.direction, initialIndex)
        }

        // 清理函数
        return () => {
            flipAnimRefs.current.forEach((anim) => {
                anim.pause()
            })
            flipAnimRefs.current.clear()
            isWhiteRefs.current.clear()
        }
    }, [chessRefs, configs, started])
}

